// localStorage outbox for unflushed match outcome reports.
//
// When a network error eats a `reportMatchOutcome` call, the payload
// goes here so the next app launch can re-submit. Each room can have
// at most one pending report (PRIMARY KEY (roomCode, playerId) on the
// server makes retries idempotent). Entries older than the TTL are
// dropped on read. Past that point the server's arbitration window
// has long since timed out and the match was resolved without us.

const STORAGE_KEY = 'valkenhall-pending-match-reports';
const ENTRY_TTL_MS = 24 * 60 * 60 * 1000;

function readAll() {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(entries) {
  if (typeof localStorage === 'undefined') return;
  try {
    if (entries.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    }
  } catch {
    // Quota errors etc.. Best-effort outbox, don't crash the renderer.
  }
}

function freshEntries() {
  const cutoff = Date.now() - ENTRY_TTL_MS;
  return readAll().filter((e) => typeof e?.queuedAt === 'number' && e.queuedAt >= cutoff);
}

export function savePendingReport({ roomCode, payload }) {
  if (!roomCode || !payload) return;
  const entries = freshEntries().filter((e) => e.roomCode !== roomCode);
  entries.push({ roomCode, payload, queuedAt: Date.now() });
  writeAll(entries);
}

export function removePendingReport(roomCode) {
  if (!roomCode) return;
  const entries = freshEntries().filter((e) => e.roomCode !== roomCode);
  writeAll(entries);
}

export function listPendingReports() {
  return freshEntries();
}

/**
 * Attempt to re-submit every queued report. Each call is independent
 * A 4xx clears the entry, a network error keeps it for the next
 * drain. Returns the count of successfully drained entries.
 *
 * @param {(payload: object & { roomCode: string }) => Promise<any>} submit
 *   Caller supplies the actual POST. Decoupled so the outbox stays
 *   pure storage and the caller controls the API surface.
 */
export async function drainPendingReports(submit) {
  const entries = freshEntries();
  if (entries.length === 0) return 0;

  let drained = 0;
  for (const entry of entries) {
    try {
      await submit({ roomCode: entry.roomCode, ...entry.payload });
      removePendingReport(entry.roomCode);
      drained++;
    } catch (err) {
      // 4xx clears the entry inside reportMatchOutcome (no_room etc).
      // Anything else leaves it for the next launch.
      console.warn('[pendingReports] drain failed for', entry.roomCode, err?.message || err);
    }
  }
  return drained;
}
